import { useEffect, useMemo, useRef, useState } from 'react';
import type { FC } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import { ComboboxPopup } from '@/components/form/inputs/combobox/ComboboxPopup';
import { Badge } from '@/components/ui/badge';
import { useCombobox } from '@/components/form/inputs/combobox/useCombobox';
import { useTranslation } from '@/hooks/useTranslation';
import { matchesReference } from '../hooks/useAdaptcutTools';
import { adaptcutToolLine } from '../lib/wearFormat';
import type { AdaptcutTool } from '../type/WearTypes';

interface AdaptcutToolPickerProps {
  tools: AdaptcutTool[];
  /** Traceability reference of the selected tool: matching Adaptcut tools come first. */
  reference: string | null;
  value: AdaptcutTool | null;
  onChange: (tool: AdaptcutTool) => void;
}

/**
 * Searchable list of the Adaptcut tools (name, reference, cutting diameter, teeth). List,
 * keyboard and outside-click handling come from the shared combobox core.
 */
export const AdaptcutToolPicker: FC<AdaptcutToolPickerProps> = ({
  tools,
  reference,
  value,
  onChange,
}) => {
  const { t, language } = useTranslation();
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);

  const options = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return tools
      .filter(
        (tool) =>
          !needle ||
          [tool.name, tool.reference].some((text) => text?.toLowerCase().includes(needle))
      )
      .sort(
        (a, b) => Number(matchesReference(b, reference)) - Number(matchesReference(a, reference))
      );
  }, [tools, query, reference]);

  const combobox = useCombobox<AdaptcutTool>({
    id: 'adaptcut-tool',
    options,
    onPick: (tool) => {
      onChange(tool);
      triggerRef.current?.focus();
    },
  });
  const { open } = combobox;

  // A closed panel forgets its search.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  function toggle() {
    if (open) {
      combobox.close();
      return;
    }
    combobox.setOpen(true);
    // Open on the chosen tool, so the keyboard starts from it (query is empty: same order).
    combobox.setActive(options.findIndex((tool) => tool.id === value?.id));
  }

  const sameReference = (tool: AdaptcutTool) =>
    matchesReference(tool, reference) ? (
      <Badge tone="primary" size="sm" caps>
        {t('mlWear.context.adaptcutSameReference')}
      </Badge>
    ) : null;

  return (
    <div ref={combobox.containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        data-testid="adaptcut-tool-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? combobox.listboxId : undefined}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            toggle();
          } else if (event.key === 'Escape' && open) {
            combobox.close();
          }
        }}
        className="flex min-h-9 w-full items-center justify-between gap-2 rounded-lg border border-input bg-transparent px-3 py-1.5 text-left text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30"
      >
        {value ? (
          <span className="flex min-w-0 flex-col">
            <span className="flex flex-wrap items-center gap-1.5">
              <span className="truncate">{value.name ?? '—'}</span>
              {value.reference && (
                <span className="text-xs text-muted-foreground">· {value.reference}</span>
              )}
              {sameReference(value)}
            </span>
            <span className="text-xs tabular-nums text-muted-foreground">
              {adaptcutToolLine(value, language, t)}
            </span>
          </span>
        ) : (
          <span className="text-muted-foreground">{t('mlWear.context.adaptcutPlaceholder')}</span>
        )}
        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
      </button>
      {open && (
        <ComboboxPopup
          combobox={combobox}
          options={options}
          optionKey={(tool) => String(tool.id)}
          emptyLabel={t('mlWear.context.adaptcutNoMatch')}
          listLabel={t('mlWear.context.adaptcutList')}
          top={
            <div className="border-b border-border/40 p-2">
              <input
                autoFocus
                type="search"
                role="combobox"
                data-testid="adaptcut-tool-search"
                aria-autocomplete="list"
                aria-expanded
                aria-controls={combobox.listboxId}
                aria-activedescendant={
                  combobox.activeIndex >= 0 ? combobox.optionId(combobox.activeIndex) : undefined
                }
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  combobox.setActive(0);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') triggerRef.current?.focus();
                  combobox.onKeyDown(event);
                }}
                placeholder={t('mlWear.context.adaptcutSearch')}
                aria-label={t('mlWear.context.adaptcutSearch')}
                className="w-full rounded-lg border border-input bg-transparent px-3 py-1.5 text-sm text-foreground outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
              />
            </div>
          }
          renderOption={(tool) => (
            <span className="flex w-full min-w-0 items-center justify-between gap-2">
              <span className="flex min-w-0 items-center gap-2">
                <Check
                  className={clsx(
                    'h-4 w-4 shrink-0 text-primary',
                    value?.id !== tool.id && 'invisible'
                  )}
                  aria-hidden
                />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">
                    {tool.name ?? '—'}{' '}
                    {tool.reference && (
                      <span className="text-xs text-muted-foreground">· {tool.reference}</span>
                    )}
                  </span>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {adaptcutToolLine(tool, language, t)}
                  </span>
                </span>
              </span>
              {sameReference(tool)}
            </span>
          )}
        />
      )}
    </div>
  );
};
